import {
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { useConstant } from "@hooks/useConstant";
import { Drawer } from "@design-system/Drawer/Drawer";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { Pagination } from "@design-system/Pagination/Pagination";
import { StepTrailBreadcrumbs } from "@components/breadcrumbs/StepTrailBreadcrumbs";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { ArrowCornerIcon } from "@icons";
import { View } from "@common/types/views/views";
import { useGetHistoryDetailsQuery } from "@api/auditLog.api";
import { useHistoryDetailsListParams } from "./useHistoryDetailsListParams";
import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
    isHistoryDetailsResponse,
    RULE_RESOURCE_TYPES,
    ResourceType,
} from "@common/types/auditLog";
import {
    COA_ACTIVITY_SOURCE_TYPES,
    getSourceTypeTemplate,
    HistoryField,
    toInternalHistorySourceType,
} from "@common/constants/history-source-types";
import { pluralizeWithCount } from "@common/helpers/string";
import { Category } from "@common/types/category";
import { FinancialAccount } from "@common/types/financialAccount";
import { Entity } from "@common/types/entity";
import { Subaccount } from "@common/types/subaccount";
import { RuleGroupType } from "@common/types/transactionRule";
import { addTimeZoneOffset } from "@helpers/date";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { APIProcessName } from "@common/constants/process-names";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { getNumberField, getStringField } from "../utils/resourceFields";
import {
    getEffectiveCount,
    resolveHeaderVerb,
} from "../utils/getActivityDescription";
import { historyDrawerContext } from "../historyDrawerContext";
import { getShowRevertedParam } from "../views/historyViewConfig";
import {
    isRevertableSourceType,
    RevertSourceTarget,
    useHistorySourceRevert,
} from "../hooks/useHistorySourceRevert";
import { RevertPreviewQuestion } from "../components/RevertPreviewQuestion";
import { useTransactionSelectionActions } from "./transactions/useTransactionSelectionActions";
import { ActivityDrawerHeader } from "./ActivityDrawerHeader";
import {
    filterDisplayableDiffs,
    getFieldLabel,
    HistoryTransactionDetailsRow,
} from "./transactions/views/historyTransactionDetailsViewConfig";
import {
    hasDisplayableChanges,
    excludeCategoryDiffs,
    getRuleGroupDisplayName,
    resolveRuleGroupNameForLogs,
    buildRuleGroupUpdateDetailsRow,
    buildAccrualSnapshot,
    buildAccrualLineRows,
    buildTransactionSnapshot,
    buildDiffSideSnapshotMap,
    AccrualRowContext,
    getHistoryFieldLabel,
} from "./utils/historyDetailsHelpers";
import {
    filterFinancialAccountDisplayableDiffs,
    getFinancialAccountFieldLabel,
    HistoryFinancialAccountDetailsRow,
} from "./financialAccounts/views/historyFinancialAccountDetailsViewConfig";
import styles from "./HistoryDetails.module.scss";
import { HistoryUpdatedTransactionDetails } from "./HistoryUpdatedTransactionDetails";
import { HistoryFinancialAccountDetails } from "./HistoryFinancialAccountDetails";
import { HistoryCreatedTransactionDetails } from "./HistoryCreatedTransactionDetails";
import { HistoryCreatedTransactionDetailsRow } from "./transactions/views/historyCreatedTransactionDetailsViewConfig";
import { HistoryPairedTransferDetails } from "./pairedTransfers/HistoryPairedTransferDetails";
import {
    getPairKey,
    getTransferPairs,
} from "./pairedTransfers/transferPairHelpers";
import { HistoryAccrualMatchDetails } from "./accrualMatches/HistoryAccrualMatchDetails";
import { HistoryJournalEntryDetails } from "./journalEntries/HistoryJournalEntryDetails";
import { HistoryCounterpartyDetails } from "./counterparty/HistoryCounterpartyDetails";
import {
    filterCounterpartyDisplayableDiffs,
    getCounterpartyFieldLabel,
    CounterpartyVersion,
} from "./counterparty/views/historyCounterpartyDetailsViewConfig";
import { HistoryRuleDetails } from "./rules/HistoryRuleDetails";
import {
    getRuleFieldLabel,
    RuleVersion,
} from "./rules/views/historyRuleDetailsViewConfig";
import { extractConditionsForListRow } from "./rules/utils/ruleHistoryList.utils";
import {
    ruleHistoryListViewConfig,
    RuleHistoryListRow,
} from "./rules/views/ruleHistoryListViewConfig";
import { defaultRuleHistoryListView } from "./rules/views/defaultRuleHistoryListView";
import { defaultTransferRuleHistoryListView } from "./rules/views/defaultTransferRuleHistoryListView";
import { transferRuleHistoryListViewConfig } from "./rules/views/transferRuleHistoryListViewConfig";
import {
    HistoryRuleGroupCreateDeleteDetails,
    HistoryRuleGroupUpdateDetails,
} from "./HistoryRuleGroupDetails";
import {
    HistoryClassCreateDeleteDetails,
    HistoryClassUpdateDetails,
} from "./HistoryClassDetails";
import { HistoryAccountDetails } from "./HistoryAccountDetails";
import {
    HistoryTaskCreateDeleteDetails,
    HistoryTaskUpdateDetails,
} from "./task/HistoryTaskDetails";
import {
    buildTaskUpdateDetailsRow,
    getTaskFieldLabel,
} from "./task/utils/historyTaskRows";
import { HistoryTaskUpdateDetailsRow } from "./task/views/historyTaskUpdateDetailsViewConfig";
import {
    HistoryRuleGroupCreateDeleteDetailsRow,
    HistoryRuleGroupUpdateDetailsRow,
} from "./ruleGroups/views/historyRuleGroupDetailsViewConfig";
import {
    buildClassCreateDeleteDetailsRow,
    buildClassUpdateDetailsRow,
} from "./classes/utils/historyClassDetailsRows";
import {
    HistoryClassCreateDeleteDetailsRow,
    HistoryClassUpdateDetailsRow,
} from "./classes/views/historyClassDetailsViewConfig";
import {
    buildHistoryAccountTable,
    getAccountFieldLabel,
} from "./accounts/utils/historyAccountDetailsRows";
import { HistoryAccountTableKind } from "./accounts/views/historyAccountDetailsView";
import {
    formatCodeAndNameIdentifier,
    formatNameIdentifier,
} from "./accounts/utils/coaActivityValues";
import {
    HistoryDocumentCreateDeleteDetails,
    HistoryDocumentDownloadDetails,
    HistoryDocumentUpdateDetails,
} from "./documents/HistoryDocumentDetails";
import {
    buildDocumentDownloadDetailsRows,
    HistoryDocumentDownloadDetailsRow,
} from "./documents/utils/historyDocumentDownloadRows";
import {
    buildDocumentCreateDeleteDetailsRow,
    buildDocumentUpdateDetailsRows,
    HistoryDocumentCreateDeleteDetailsRow,
    HistoryDocumentUpdateDetailsRow,
} from "./documents/utils/historyDocumentDetailsRow";
import { HistoryCatalogItemDetails } from "./HistoryCatalogItemDetails";
import { useHistoryCatalogItemTable } from "./catalogItems/hooks/useHistoryCatalogItemTable";
import splitDrawerStyles from "./split/SplitHistoryDrawer.module.scss";
import { FieldReviewDrawerContent } from "./FieldReviewDrawerContent";
import { HistoryInvoiceTemplateDrawerContent } from "./invoiceTemplates/HistoryInvoiceTemplateDrawerContent";

const PAGE_SIZE = 20;
const COA_GESTURE_CHANGE_TYPES = Object.values(AuditLogChangeType);

const RULE_HISTORY_BREADCRUMB_STEPS = [
    { id: "list" as const, label: "Rules" },
    { id: "details" as const, label: "Details" },
];

interface DrawerFilters {
    search?: string;
    entityIds?: number[];
}

const ViewTransactionsButton: React.FC<{
    selectedCount: number;
    onClick: () => void;
}> = ({ selectedCount, onClick }) => {
    const label =
        selectedCount > 0
            ? `View ${selectedCount} selected`
            : "View all transactions";

    return (
        <Button
            variant="primary"
            size="md"
            onClick={onClick}
            leftIcon={<ArrowCornerIcon />}
        >
            {label}
        </Button>
    );
};

export const HistoryDetailsDrawer: React.FC = () => {
    const { open, historyRow, closeDrawer } = useContext(historyDrawerContext);
    const [isOpen, setIsOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState<DrawerFilters>({});
    const [selectedRuleLogId, setSelectedRuleLogId] = useState<string | null>(
        null,
    );
    const [ruleListView, setRuleListView] = useState<View>(
        defaultRuleHistoryListView,
    );
    const [transferRuleListView, setTransferRuleListView] = useState<View>(
        defaultTransferRuleHistoryListView,
    );
    const [transactionRowSelection, setTransactionRowSelection] =
        useState<RowSelectionState>({});
    const [selectedPairKeys, setSelectedPairKeys] = useState<Set<string>>(
        new Set(),
    );
    const openTransactionViewDrawer = useGlobalTransactionViewDrawer();
    const { enabled: isGlFirst } = useIsGlFirst();
    const showAccountNumbers = useShowAccountNumbers();
    const listParams = useHistoryDetailsListParams(historyRow);
    const fallbackDate = useConstant(() => new Date());

    const {
        revertTarget,
        requestRevert,
        cancelRevert,
        confirmRevert,
        previewCount,
        previewFields,
        isPreviewLoading,
    } = useHistorySourceRevert<RevertSourceTarget>(historyRow);

    const shouldShowContent = isOpen || open;
    const isCoaGesture =
        !!historyRow &&
        COA_ACTIVITY_SOURCE_TYPES.includes(historyRow.sourceType) &&
        historyRow.historyItem.sourceIds.length > 0;
    const {
        data: historyDetailsResponse,
        isLoading,
        isError: isDetailsError,
        refetch: refetchDetails,
    } = useGetHistoryDetailsQuery({
        ...listParams,
        date: historyRow?.date ?? fallbackDate,
        sourceType: historyRow?.sourceType ?? "",
        resourceType: isCoaGesture ? undefined : historyRow?.resourceType,
        actorId: historyRow?.actorId,
        organizationActorId: historyRow?.historyItem.organizationActorId,
        entityIds: historyRow?.entityIds,
        changeTypes: isCoaGesture
            ? COA_GESTURE_CHANGE_TYPES
            : historyRow?.changeTypes,
        fields: historyRow?.fields,
        sourceIds: historyRow?.historyItem.sourceIds,
        search: filters.search,
        page: currentPage,
        limit: PAGE_SIZE,
        showReverted: getShowRevertedParam(historyRow),
        options: {
            enabled:
                shouldShowContent && !!historyRow && !!historyRow.sourceType,
        },
    });

    const detailsResponse =
        historyDetailsResponse &&
        isHistoryDetailsResponse(historyDetailsResponse)
            ? historyDetailsResponse
            : undefined;

    const totalPages = detailsResponse?.totalPages ?? 1;
    const totalCount = detailsResponse?.total ?? 0;
    const detailsLogs = detailsResponse?.data;

    const selectedFields = useMemo(
        () => historyRow?.fields ?? [],
        [historyRow?.fields],
    );

    const isFinancialAccount =
        historyRow?.resourceType === ResourceType.FINANCIAL_ACCOUNT;

    const isCounterparty =
        historyRow?.resourceType === ResourceType.COUNTERPARTY;

    const isTransactionRule = historyRow?.resourceType
        ? RULE_RESOURCE_TYPES.has(historyRow.resourceType)
        : false;

    const isTransactionRuleGroup =
        historyRow?.resourceType === ResourceType.TRANSACTION_RULE_GROUP;
    const isClass = historyRow?.resourceType === ResourceType.CLASS;
    const isCatalogItem =
        historyRow?.resourceType === ResourceType.CATALOG_ITEM;
    const isAccount =
        historyRow?.resourceType === ResourceType.ACCOUNT ||
        historyRow?.resourceType === ResourceType.ACCOUNT_GROUP;
    const accountEmptyStateHeader =
        historyRow?.resourceType === ResourceType.ACCOUNT_GROUP
            ? "No account group changes found"
            : "No account changes found";
    const isDocument =
        historyRow?.resourceType === ResourceType.FINANCIAL_DOCUMENT;
    const isTask = historyRow?.resourceType === ResourceType.TASK;
    const isTransactionFieldReview =
        historyRow?.resourceType === ResourceType.TRANSACTION_FIELD_REVIEW;
    const isInvoiceTemplate =
        historyRow?.resourceType === ResourceType.INVOICE_TEMPLATE;

    const isJournalEntry =
        historyRow?.resourceType === ResourceType.JOURNAL_ENTRY ||
        historyRow?.resourceType === ResourceType.MANUAL_JOURNAL_ENTRY;

    const isAccrual =
        historyRow?.resourceType === ResourceType.BILL ||
        historyRow?.resourceType === ResourceType.INVOICE;

    const isAccrualMatch =
        historyRow?.resourceType === ResourceType.ACCRUAL_MATCH;

    const isTransfer = useMemo(
        () =>
            selectedFields.length === 1 &&
            selectedFields[0] === HistoryField.PAIRED_TRANSFER_ID,
        [selectedFields],
    );

    const isMatched = detailsResponse?.data?.some(
        (log: HistoryDetailsItem) =>
            log.changeType === AuditLogChangeType.UPDATE &&
            log.diff.some(
                (diff: AuditLogDiff) =>
                    diff.field === HistoryField.PAIRED_TRANSFER_ID &&
                    !!diff.after.value,
            ),
    );

    const isUpdated = detailsResponse?.data?.some(
        (log: HistoryDetailsItem) =>
            AuditLogChangeType.UPDATE === log.changeType,
    );
    const isCreated = detailsResponse?.data?.some(
        (log: HistoryDetailsItem) =>
            AuditLogChangeType.CREATE === log.changeType,
    );
    const isDeleted = detailsResponse?.data?.some(
        (log: HistoryDetailsItem) =>
            AuditLogChangeType.DELETE === log.changeType,
    );
    const isTransactionDelete =
        historyRow?.resourceType === ResourceType.TRANSACTION &&
        !isAccrual &&
        !isUpdated &&
        !isCreated &&
        !!isDeleted;

    const updatedTransactionTableData = useMemo<
        HistoryTransactionDetailsRow[]
    >(() => {
        if (isFinancialAccount || !detailsLogs) {
            return [];
        }
        if (
            isTransactionRuleGroup ||
            isClass ||
            isAccount ||
            isTask ||
            isTransactionFieldReview ||
            isInvoiceTemplate
        ) {
            return [];
        }
        if (!isUpdated && !isAccrual && !isTransactionDelete) {
            return [];
        }

        return detailsLogs
            .filter(hasDisplayableChanges)
            .flatMap((log: HistoryDetailsItem) => {
                let displayableDiffs = filterDisplayableDiffs(log.diff);

                if (selectedFields.length > 0) {
                    displayableDiffs = displayableDiffs.filter(
                        (diff: AuditLogDiff) =>
                            selectedFields.includes(diff.field),
                    );
                }

                if (isGlFirst) {
                    displayableDiffs = excludeCategoryDiffs(displayableDiffs);
                }

                const shouldShowEmptyRow =
                    displayableDiffs.length === 0 &&
                    selectedFields.length === 0;

                const resolvedAmount =
                    getNumberField(log.resource, "amount") ??
                    getNumberField(log.resource, "total") ??
                    0;
                const resourceDate = getStringField(log.resource, "date");
                const resourceDue = getStringField(log.resource, "due");
                let resolvedDate: Date = log.createdAt;
                if (resourceDate) {
                    resolvedDate = addTimeZoneOffset(parseISO(resourceDate));
                } else if (resourceDue) {
                    resolvedDate = addTimeZoneOffset(parseISO(resourceDue));
                }

                if (
                    log.resourceType === ResourceType.TRANSACTION &&
                    log.changeType === AuditLogChangeType.DELETE
                ) {
                    const beforeSnapshot = buildDiffSideSnapshotMap(
                        log.diff,
                        "before",
                    );
                    const beforeAmount =
                        getNumberField(beforeSnapshot, "amount") ?? 0;
                    const beforeDateStr = getStringField(
                        beforeSnapshot,
                        "date",
                    );
                    const beforeDate = beforeDateStr
                        ? addTimeZoneOffset(parseISO(beforeDateStr))
                        : log.createdAt;
                    const beforeFinancialAccountId = getNumberField(
                        beforeSnapshot,
                        "financialAccountId",
                    );
                    const beforeEntityId = getNumberField(
                        beforeSnapshot,
                        "entityId",
                    );
                    const snapshotLabel = buildTransactionSnapshot(
                        log.diff,
                        "before",
                    );
                    const snapshotValue: AuditLogDiff["before"] = {
                        value: snapshotLabel || "Deleted",
                        valueLabel: undefined,
                    };
                    const emptyValue = {
                        value: null,
                        valueLabel: undefined,
                    };
                    return [
                        {
                            id: log.id,
                            logDate: log.createdAt,
                            date: beforeDate,
                            changeType: log.changeType,
                            resourceType: log.resourceType,
                            resourceId: log.resourceId,
                            resource: log.resource,
                            field: "",
                            fieldLabel: "Deleted",
                            oldValue: snapshotValue,
                            newValue: emptyValue,
                            financialAccountId: beforeFinancialAccountId,
                            amount: beforeAmount,
                            entityId: beforeEntityId ?? log.entityId,
                        },
                    ];
                }

                if (
                    isAccrual &&
                    (log.changeType === AuditLogChangeType.CREATE ||
                        log.changeType === AuditLogChangeType.DELETE)
                ) {
                    const isDelete =
                        log.changeType === AuditLogChangeType.DELETE;
                    const side: "before" | "after" = isDelete
                        ? "before"
                        : "after";
                    const snapshotLabel = buildAccrualSnapshot(log.diff, side);
                    const snapshotValue: AuditLogDiff["before"] = {
                        value:
                            snapshotLabel || (isDelete ? "Deleted" : "Created"),
                        valueLabel: undefined,
                    };
                    const emptyValue = { value: null, valueLabel: undefined };
                    return [
                        {
                            id: log.id,
                            logDate: log.createdAt,
                            date: resolvedDate,
                            changeType: log.changeType,
                            resourceType: log.resourceType,
                            resourceId: log.resourceId,
                            resource: log.resource,
                            field: "",
                            fieldLabel: isDelete ? "Deleted" : "Created",
                            oldValue: isDelete ? snapshotValue : emptyValue,
                            newValue: isDelete ? emptyValue : snapshotValue,
                            financialAccountId:
                                log.resource?.financialAccountId,
                            amount: resolvedAmount,
                            entityId: log.resource?.entityId ?? log.entityId,
                        },
                    ];
                }

                if (shouldShowEmptyRow) {
                    return [
                        {
                            id: log.id,
                            logDate: log.createdAt,
                            date: resolvedDate,
                            changeType: log.changeType,
                            resourceType: log.resourceType,
                            resourceId: log.resourceId,
                            resource: log.resource,
                            field: "",
                            fieldLabel: "",
                            oldValue: { value: null, valueLabel: undefined },
                            newValue: { value: null, valueLabel: undefined },
                            financialAccountId:
                                log.resource?.financialAccountId,
                            amount: resolvedAmount,
                            entityId: log.resource?.entityId ?? log.entityId,
                        },
                    ];
                }

                const rowContext: AccrualRowContext = {
                    log,
                    resolvedDate,
                    resolvedAmount,
                };

                return displayableDiffs.flatMap(
                    (diff: AuditLogDiff): HistoryTransactionDetailsRow[] => {
                        if (diff.field === "lines" && isAccrual) {
                            return buildAccrualLineRows(diff, rowContext);
                        }

                        return [
                            {
                                id: `${log.id}:${diff.field}`,
                                logDate: log.createdAt,
                                date: resolvedDate,
                                changeType: log.changeType,
                                resourceType: log.resourceType,
                                resourceId: log.resourceId,
                                resource: log.resource,
                                field: diff.field,
                                fieldLabel: getFieldLabel(diff.field) ?? "",
                                oldValue: diff.before,
                                newValue: diff.after,
                                financialAccountId:
                                    log.resource?.financialAccountId,
                                amount: resolvedAmount,
                                entityId:
                                    log.resource?.entityId ?? log.entityId,
                            },
                        ];
                    },
                );
            });
    }, [
        detailsLogs,
        isUpdated,
        isAccrual,
        isTransactionDelete,
        selectedFields,
        isFinancialAccount,
        isTransactionRuleGroup,
        isClass,
        isAccount,
        isTask,
        isTransactionFieldReview,
        isInvoiceTemplate,
        isGlFirst,
    ]);

    const createdTransactionTableData = useMemo<
        HistoryCreatedTransactionDetailsRow[]
    >(() => {
        if (
            isFinancialAccount ||
            !detailsLogs ||
            !isCreated ||
            isTransactionRuleGroup ||
            isClass ||
            isAccount ||
            isTask ||
            isTransactionFieldReview ||
            isInvoiceTemplate
        ) {
            return [];
        }

        return detailsLogs
            .filter(hasDisplayableChanges)
            .map((log: HistoryDetailsItem) => {
                const entityFromDiff = log.diff.find(
                    (diff: AuditLogDiff) => diff.field === "entity",
                )?.after.value as Partial<Entity> | undefined;
                const entityIdForRow =
                    log.resource?.entityId ?? log.entityId ?? null;

                const rawIdValue =
                    log.diff.find((diff: AuditLogDiff) => diff.field === "id")
                        ?.after.value ?? log.resourceId;
                const id =
                    rawIdValue === null || rawIdValue === undefined
                        ? ""
                        : String(rawIdValue);
                const canOpenTransaction =
                    id !== "" && Number.isFinite(Number(id));

                return {
                    canOpenTransaction,
                    id,
                    logDate: log.createdAt,
                    date: addTimeZoneOffset(
                        new Date(
                            log.diff.find(
                                (diff: AuditLogDiff) => diff.field === "date",
                            )?.after.value as string,
                        ),
                    ),
                    bankDescription: log.diff.find(
                        (diff: AuditLogDiff) =>
                            diff.field === "bankDescription",
                    )?.after.value as string,
                    category: log.diff.find(
                        (diff: AuditLogDiff) => diff.field === "category",
                    )?.after.value as Partial<Category>,
                    financialAccount: {
                        id: Number(
                            log.diff.find(
                                (diff: AuditLogDiff) =>
                                    diff.field === "financialAccountId",
                            )?.after.value,
                        ),
                        name: log.diff.find(
                            (diff: AuditLogDiff) =>
                                diff.field === "financialAccountId",
                        )?.after.valueLabel,
                    } as Partial<FinancialAccount>,
                    amount: log.diff.find(
                        (diff: AuditLogDiff) => diff.field === "amount",
                    )?.after.value as number,
                    originalAmount: log.diff.find(
                        (diff: AuditLogDiff) => diff.field === "originalAmount",
                    )?.after.value as number,
                    entity:
                        entityFromDiff ??
                        (entityIdForRow != null
                            ? { id: entityIdForRow }
                            : ({} as Partial<Entity>)),
                    subaccount: log.diff.find(
                        (diff: AuditLogDiff) => diff.field === "subaccount",
                    )?.after.value as Partial<Subaccount>,
                    ledger: {
                        accountId: log.diff.find(
                            (diff: AuditLogDiff) => diff.field === "accountId",
                        )?.after.value as string,
                        accountLabel: log.diff.find(
                            (diff: AuditLogDiff) => diff.field === "accountId",
                        )?.after.valueLabel as string,
                    },
                    entityId: entityIdForRow,
                };
            });
    }, [
        detailsLogs,
        isCreated,
        isFinancialAccount,
        isTransactionRuleGroup,
        isClass,
        isAccount,
        isTask,
        isTransactionFieldReview,
        isInvoiceTemplate,
    ]);

    const financialAccountTableData = useMemo<
        HistoryFinancialAccountDetailsRow[]
    >(() => {
        if (!isFinancialAccount || !detailsLogs) {
            return [];
        }

        return detailsLogs
            .filter(hasDisplayableChanges)
            .flatMap((log: HistoryDetailsItem) => {
                let displayableDiffs = filterFinancialAccountDisplayableDiffs(
                    log.diff,
                );

                if (selectedFields.length > 0) {
                    displayableDiffs = displayableDiffs.filter(
                        (diff: AuditLogDiff) =>
                            selectedFields.includes(diff.field),
                    );
                }

                const shouldShowEmptyRow =
                    displayableDiffs.length === 0 &&
                    selectedFields.length === 0;

                const entityDate = log.resource?.date
                    ? addTimeZoneOffset(new Date(log.resource.date as string))
                    : log.createdAt;

                if (shouldShowEmptyRow) {
                    return [
                        {
                            id: log.id,
                            logDate: log.createdAt,
                            date: entityDate,
                            changeType: log.changeType,
                            resourceType: log.resourceType,
                            resourceId: log.resourceId,
                            resource: log.resource,
                            accountName: log.resource?.name as string,
                            field: "",
                            fieldLabel: "",
                            oldValue: { value: null, valueLabel: undefined },
                            newValue: { value: null, valueLabel: undefined },
                            entityId: log.resource?.entityId ?? log.entityId,
                        },
                    ];
                }

                return displayableDiffs.map((diff: AuditLogDiff) => ({
                    id: log.id,
                    logDate: log.createdAt,
                    date: entityDate,
                    changeType: log.changeType,
                    resourceType: log.resourceType,
                    resourceId: log.resourceId,
                    resource: log.resource,
                    accountName: log.resource?.name as string,
                    field: diff.field,
                    fieldLabel: getFinancialAccountFieldLabel(diff.field) ?? "",
                    oldValue: diff.before,
                    newValue: diff.after,
                    entityId: log.resource?.entityId ?? log.entityId,
                }));
            });
    }, [detailsLogs, selectedFields, isFinancialAccount]);

    const filteredUpdatedTransactionData = useMemo<
        HistoryTransactionDetailsRow[]
    >(() => {
        let result = updatedTransactionTableData;

        const hasEntityFilter =
            filters.entityIds && filters.entityIds?.length > 0;
        if (hasEntityFilter) {
            result = result.filter(
                (row) =>
                    row.entityId && filters.entityIds?.includes(row.entityId),
            );
        }

        return result;
    }, [updatedTransactionTableData, filters.entityIds]);

    const filteredCreatedTransactionData = useMemo<
        HistoryCreatedTransactionDetailsRow[]
    >(() => {
        let result = createdTransactionTableData;

        const hasEntityFilter =
            filters.entityIds && filters.entityIds?.length > 0;
        if (hasEntityFilter) {
            result = result.filter(
                (row) =>
                    row.entityId !== null &&
                    row.entityId !== undefined &&
                    filters.entityIds?.includes(row.entityId),
            );
        }

        return result;
    }, [createdTransactionTableData, filters.entityIds]);

    const useLifecycleSelectionForTransactions =
        historyRow?.resourceType === ResourceType.TRANSACTION &&
        isCreated &&
        !isUpdated &&
        !isAccrual &&
        createdTransactionTableData.length > 0;

    const { selectedTransactionIds, openTransactions: handleOpenTransactions } =
        useTransactionSelectionActions(
            useLifecycleSelectionForTransactions
                ? {
                      mode: "lifecycle",
                      rows: filteredCreatedTransactionData,
                      rowSelection: transactionRowSelection,
                      historyRow: historyRow ?? null,
                      sourceIds: historyRow?.historyItem.sourceIds,
                  }
                : {
                      mode: "diff",
                      rows: filteredUpdatedTransactionData,
                      rowSelection: transactionRowSelection,
                      historyRow: historyRow ?? null,
                      sourceIds: historyRow?.historyItem.sourceIds,
                  },
        );

    const canOpenTransactions = useMemo(
        () =>
            !!historyRow &&
            (((isUpdated || isAccrual) &&
                updatedTransactionTableData.some(
                    (row) => row.resourceType === ResourceType.TRANSACTION,
                )) ||
                (historyRow.resourceType === ResourceType.TRANSACTION &&
                    isCreated &&
                    !isUpdated &&
                    !isAccrual &&
                    createdTransactionTableData.some(
                        (row) => row.canOpenTransaction,
                    ))),
        [
            historyRow,
            isUpdated,
            isAccrual,
            updatedTransactionTableData,
            isCreated,
            createdTransactionTableData,
        ],
    );

    const counterpartyVersions = useMemo<CounterpartyVersion[]>(() => {
        if (!isCounterparty || !detailsLogs) {
            return [];
        }

        return detailsLogs.map((log: HistoryDetailsItem) => {
            const diffs = filterCounterpartyDisplayableDiffs(log.diff);
            const nameDiff = log.diff.find((d) => d.field === "name");
            const counterpartyName =
                (log.resource?.name as string) ??
                (nameDiff?.after.value as string) ??
                (nameDiff?.before.value as string) ??
                "";

            return {
                versionId: log.id,
                logDate: log.createdAt,
                changeType: log.changeType,
                counterpartyName,
                diffs,
            };
        });
    }, [detailsLogs, isCounterparty]);

    const ruleVersions = useMemo<RuleVersion[]>(() => {
        if (!isTransactionRule || !detailsLogs) {
            return [];
        }

        return detailsLogs.map((log: HistoryDetailsItem) => {
            const groupName = (log.resource?.group as Record<string, unknown>)
                ?.name as string | undefined;

            return {
                versionId: log.id,
                logDate: log.createdAt,
                changeType: log.changeType,
                resourceType: log.resourceType,
                ruleGroupName: groupName ?? "Rule",
                diffs: log.diff,
                resource: log.resource,
                metadata: log.metadata,
            };
        });
    }, [detailsLogs, isTransactionRule]);

    const ruleHistoryListRows = useMemo<RuleHistoryListRow[]>(() => {
        if (!isTransactionRule || !detailsLogs) {
            return [];
        }

        const grouped = new Map<string, HistoryDetailsItem[]>();
        for (const log of detailsLogs) {
            const list = grouped.get(log.resourceId) ?? [];
            list.push(log);
            grouped.set(log.resourceId, list);
        }

        return Array.from(grouped.entries()).map(([resourceId, logs]) => {
            const latest = logs[0];
            const group = latest.resource?.group as
                | Record<string, unknown>
                | undefined;
            const ruleGroupName = resolveRuleGroupNameForLogs(logs);
            const ruleType =
                (group?.type as RuleGroupType) ?? RuleGroupType.TRANSACTION;

            let conditions = [] as RuleHistoryListRow["conditions"];
            let conditionTexts = [] as RuleHistoryListRow["conditionTexts"];
            for (const log of logs) {
                const candidate = extractConditionsForListRow(log);
                if (
                    candidate.conditions.length > 0 ||
                    candidate.conditionTexts.length > 0
                ) {
                    conditions = candidate.conditions;
                    conditionTexts = candidate.conditionTexts;
                    break;
                }
            }

            return {
                id: resourceId,
                logId: resourceId,
                date: new Date(latest.createdAt),
                changeType: latest.changeType,
                ruleGroupName,
                ruleType,
                ruleResourceId: resourceId,
                conditions,
                conditionTexts,
            };
        });
    }, [detailsLogs, isTransactionRule]);

    const ruleGroupCreateDeleteData = useMemo<
        HistoryRuleGroupCreateDeleteDetailsRow[]
    >(() => {
        if (!isTransactionRuleGroup || !detailsLogs) {
            return [];
        }
        return detailsLogs.filter(hasDisplayableChanges).map((log) => ({
            id: log.id,
            resourceType: log.resourceType,
            resourceId: log.resourceId,
            groupName: getRuleGroupDisplayName(log),
            logDate: log.createdAt,
            date: log.createdAt,
            entityId: log.entityId ?? null,
        }));
    }, [isTransactionRuleGroup, detailsLogs]);

    const ruleGroupUpdateData = useMemo<
        HistoryRuleGroupUpdateDetailsRow[]
    >(() => {
        if (!isTransactionRuleGroup || !detailsLogs) {
            return [];
        }
        return detailsLogs
            .filter(hasDisplayableChanges)
            .map(buildRuleGroupUpdateDetailsRow);
    }, [isTransactionRuleGroup, detailsLogs]);

    const classCreateDeleteData = useMemo<
        HistoryClassCreateDeleteDetailsRow[]
    >(() => {
        if (!isClass || !detailsLogs) {
            return [];
        }
        return detailsLogs
            .filter(hasDisplayableChanges)
            .map(buildClassCreateDeleteDetailsRow);
    }, [isClass, detailsLogs]);

    const classUpdateData = useMemo<HistoryClassUpdateDetailsRow[]>(() => {
        if (!isClass || !detailsLogs) {
            return [];
        }
        return detailsLogs
            .filter(hasDisplayableChanges)
            .map(buildClassUpdateDetailsRow);
    }, [isClass, detailsLogs]);

    const catalogItemTable = useHistoryCatalogItemTable({
        logs: detailsLogs ?? [],
        selectedFields,
        isEnabled: isCatalogItem,
    });

    const accountTable = useMemo(
        () =>
            buildHistoryAccountTable({
                logs: isAccount ? (detailsLogs ?? []) : [],
                sourceType: historyRow?.sourceType ?? "",
                selectedFields,
                formatIdentifier: showAccountNumbers
                    ? formatCodeAndNameIdentifier
                    : formatNameIdentifier,
            }),
        [
            isAccount,
            detailsLogs,
            historyRow?.sourceType,
            selectedFields,
            showAccountNumbers,
        ],
    );

    const documentCreateDeleteData = useMemo<
        HistoryDocumentCreateDeleteDetailsRow[]
    >(() => {
        if (!isDocument || !detailsLogs) {
            return [];
        }
        return detailsLogs
            .filter(hasDisplayableChanges)
            .map(buildDocumentCreateDeleteDetailsRow);
    }, [isDocument, detailsLogs]);

    const isDocumentDownload =
        isDocument &&
        !!historyRow?.changeTypes?.length &&
        historyRow.changeTypes.every(
            (changeType) => changeType === AuditLogChangeType.DOWNLOAD,
        );

    const documentDownloadData = useMemo<
        HistoryDocumentDownloadDetailsRow[]
    >(() => {
        if (!isDocumentDownload || !detailsLogs) {
            return [];
        }
        return detailsLogs.flatMap(buildDocumentDownloadDetailsRows);
    }, [isDocumentDownload, detailsLogs]);

    const documentUpdateData = useMemo<
        HistoryDocumentUpdateDetailsRow[]
    >(() => {
        if (!isDocument || !detailsLogs) {
            return [];
        }
        return detailsLogs
            .filter(hasDisplayableChanges)
            .flatMap((log) =>
                buildDocumentUpdateDetailsRows(log, selectedFields),
            );
    }, [isDocument, detailsLogs, selectedFields]);

    const taskCreateDeleteLogs = useMemo<HistoryDetailsItem[]>(() => {
        if (!isTask || !detailsLogs) {
            return [];
        }
        return detailsLogs.filter(
            (log) =>
                log.changeType === AuditLogChangeType.CREATE ||
                log.changeType === AuditLogChangeType.DELETE,
        );
    }, [isTask, detailsLogs]);

    const taskUpdateData = useMemo<HistoryTaskUpdateDetailsRow[]>(() => {
        if (!isTask || !detailsLogs) {
            return [];
        }
        return detailsLogs
            .filter(
                (log) =>
                    log.changeType === AuditLogChangeType.UPDATE &&
                    log.diff.length > 0,
            )
            .map(buildTaskUpdateDetailsRow);
    }, [isTask, detailsLogs]);

    const selectedRuleVersions = useMemo(() => {
        if (!selectedRuleLogId) {
            return [];
        }
        return ruleVersions.filter((v) => {
            const log = detailsLogs?.find(
                (l: HistoryDetailsItem) => l.id === v.versionId,
            );
            return log?.resourceId === selectedRuleLogId;
        });
    }, [ruleVersions, selectedRuleLogId, detailsLogs]);

    const filteredFinancialAccountData = useMemo<
        HistoryFinancialAccountDetailsRow[]
    >(() => {
        let result = financialAccountTableData;

        const hasEntityFilter =
            filters.entityIds && filters.entityIds?.length > 0;
        if (hasEntityFilter) {
            result = result.filter(
                (row) =>
                    row.entityId && filters.entityIds?.includes(row.entityId),
            );
        }

        return result;
    }, [financialAccountTableData, filters.entityIds]);

    const handleClose = useCallback(() => {
        setCurrentPage(1);
        setFilters({});
        setSelectedRuleLogId(null);
        setTransactionRowSelection({});
        closeDrawer();
    }, [closeDrawer]);

    const handleSelectRuleHistoryRow = useCallback(
        (row?: RuleHistoryListRow) => {
            if (!row) {
                return;
            }
            setSelectedRuleLogId(row.logId);
        },
        [],
    );

    const handleCloseRuleDetailDrawer = useCallback(() => {
        setSelectedRuleLogId(null);
    }, []);

    const handleRuleHistoryBreadcrumbStepChange = useCallback(
        (stepId: "list" | "details") => {
            if (stepId === "list") {
                handleCloseRuleDetailDrawer();
            }
        },
        [handleCloseRuleDetailDrawer],
    );

    const handleSearchChange = useCallback((value: string | undefined) => {
        setFilters((prev) => ({ ...prev, search: value ?? "" }));
        setCurrentPage(1);
    }, []);

    useEffect(() => {
        setTransactionRowSelection({});
        setSelectedPairKeys(new Set());
    }, [historyRow?.id]);

    const transferPairs = useMemo(
        () =>
            isTransfer
                ? getTransferPairs(updatedTransactionTableData, isMatched)
                : [],
        [isTransfer, updatedTransactionTableData, isMatched],
    );

    const handlePairSelectionChange = useCallback((pairKey: string) => {
        setSelectedPairKeys((prev) => {
            const next = new Set(prev);
            if (next.has(pairKey)) {
                next.delete(pairKey);
            } else {
                next.add(pairKey);
            }
            return next;
        });
    }, []);

    const selectedTransferTransactionIds = useMemo(() => {
        if (selectedPairKeys.size === 0) {
            return [];
        }
        const ids: number[] = [];
        for (const pair of transferPairs) {
            if (selectedPairKeys.has(getPairKey(pair))) {
                ids.push(pair.transaction.id, pair.pairedTransaction.id);
            }
        }
        return Array.from(new Set(ids));
    }, [selectedPairKeys, transferPairs]);

    const handleOpenTransferTransactions = useCallback(() => {
        if (!historyRow) {
            return;
        }
        if (selectedTransferTransactionIds.length > 0) {
            openTransactionViewDrawer({
                entityIds: historyRow.entityIds,
                transactionIds: selectedTransferTransactionIds,
            });
            return;
        }
        openTransactionViewDrawer({
            entityIds: historyRow.entityIds,
            auditLogFilter: {
                date: historyRow.date,
                sourceType: historyRow.sourceType,
                sourceIds: historyRow.historyItem.sourceIds,
                actorId: historyRow.actorId,
                changeTypes: historyRow.changeTypes,
                fields: historyRow.fields,
            },
        });
    }, [historyRow, selectedTransferTransactionIds, openTransactionViewDrawer]);

    const handleOpenPair = useCallback(
        (transactionIds: number[]) => {
            if (!historyRow) {
                return;
            }
            openTransactionViewDrawer({
                entityIds: historyRow.entityIds,
                transactionIds,
            });
        },
        [historyRow, openTransactionViewDrawer],
    );

    const showViewTransactionsCta = canOpenTransactions || isTransfer;
    const showFooterPagination = totalPages > 1;

    const isTransactionRevertable =
        !!historyRow &&
        historyRow.resourceType === ResourceType.TRANSACTION &&
        isRevertableSourceType(historyRow.sourceType) &&
        !historyRow.revertedAt;

    const revertableResourceIdSet = useMemo(
        () => new Set(historyRow?.historyItem.resourceIds ?? []),
        [historyRow?.historyItem.resourceIds],
    );

    const selectedRevertableResourceIds = useMemo(() => {
        if (!isTransactionRevertable) {
            return [] as string[];
        }
        return selectedTransactionIds
            .map(String)
            .filter((id) => revertableResourceIdSet.has(id));
    }, [
        isTransactionRevertable,
        selectedTransactionIds,
        revertableResourceIdSet,
    ]);

    const revertableCount =
        selectedRevertableResourceIds.length > 0
            ? selectedRevertableResourceIds.length
            : revertableResourceIdSet.size;

    const handleRequestRevert = useCallback(() => {
        if (!isTransactionRevertable || !historyRow) {
            return;
        }
        requestRevert({
            count: revertableCount,
            resourceIds:
                selectedRevertableResourceIds.length > 0
                    ? selectedRevertableResourceIds
                    : undefined,
            system: historyRow.system ?? undefined,
        });
    }, [
        isTransactionRevertable,
        historyRow,
        requestRevert,
        revertableCount,
        selectedRevertableResourceIds,
    ]);

    const resourceTemplate = historyRow
        ? getSourceTypeTemplate(
              historyRow.sourceType,
              historyRow.resourceType,
              historyRow.fields,
          )
        : undefined;
    const resourceNoun = resourceTemplate?.noun ?? "item";
    const headerResourceCount = historyRow ? getEffectiveCount(historyRow) : 0;

    const footer = (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <div className={styles.footerLeft}>
                {showFooterPagination && (
                    <Pagination
                        size="md"
                        currentPage={currentPage}
                        pageCount={totalPages}
                        onPageChange={setCurrentPage}
                    />
                )}
                <CommandIslandTotal>
                    {pluralizeWithCount("change", totalCount)}
                </CommandIslandTotal>
            </div>
            <div className={styles.footerActions}>
                {isTransactionRevertable && (
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleRequestRevert}
                    >
                        {selectedRevertableResourceIds.length > 0
                            ? `Undo ${selectedRevertableResourceIds.length} selected`
                            : `Undo all (${revertableCount})`}
                    </Button>
                )}
                {showViewTransactionsCta && (
                    <ViewTransactionsButton
                        selectedCount={
                            isTransfer
                                ? selectedTransferTransactionIds.length
                                : selectedTransactionIds.length
                        }
                        onClick={
                            isTransfer
                                ? handleOpenTransferTransactions
                                : handleOpenTransactions
                        }
                    />
                )}
            </div>
        </CommandIsland>
    );

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            );
        }

        if (isJournalEntry && detailsResponse?.data) {
            return (
                <HistoryJournalEntryDetails
                    data={detailsResponse.data}
                    footer={footer}
                />
            );
        }

        if (isFinancialAccount) {
            return (
                <HistoryFinancialAccountDetails
                    data={filteredFinancialAccountData}
                    footer={footer}
                />
            );
        }

        if (isCounterparty) {
            return (
                <HistoryCounterpartyDetails
                    versions={counterpartyVersions}
                    footer={footer}
                />
            );
        }

        if (isTransactionRuleGroup) {
            if (!detailsResponse?.data?.length) {
                return (
                    <div className={splitDrawerStyles.emptyContainer}>
                        <EmptyState header="No rule group changes found" />
                    </div>
                );
            }
            const isRuleGroupCreateOrDelete = historyRow?.changeTypes?.every(
                (ct) =>
                    ct === AuditLogChangeType.CREATE ||
                    ct === AuditLogChangeType.DELETE,
            );
            if (isRuleGroupCreateOrDelete) {
                if (ruleGroupCreateDeleteData.length === 0) {
                    return (
                        <div className={splitDrawerStyles.emptyContainer}>
                            <EmptyState header="No rule group changes found" />
                        </div>
                    );
                }
                return (
                    <HistoryRuleGroupCreateDeleteDetails
                        data={ruleGroupCreateDeleteData}
                        footer={footer}
                    />
                );
            }
            if (ruleGroupUpdateData.length === 0) {
                return (
                    <div className={splitDrawerStyles.emptyContainer}>
                        <EmptyState header="No rule group changes found" />
                    </div>
                );
            }
            return (
                <HistoryRuleGroupUpdateDetails
                    data={ruleGroupUpdateData}
                    footer={footer}
                />
            );
        }

        if (isClass) {
            if (!detailsResponse?.data?.length) {
                return (
                    <div className={splitDrawerStyles.emptyContainer}>
                        <EmptyState header="No class changes found" />
                    </div>
                );
            }
            const isClassCreateOrDelete = historyRow?.changeTypes?.every(
                (ct) =>
                    ct === AuditLogChangeType.CREATE ||
                    ct === AuditLogChangeType.DELETE,
            );
            if (isClassCreateOrDelete) {
                if (classCreateDeleteData.length === 0) {
                    return (
                        <div className={splitDrawerStyles.emptyContainer}>
                            <EmptyState header="No class changes found" />
                        </div>
                    );
                }
                return (
                    <HistoryClassCreateDeleteDetails
                        data={classCreateDeleteData}
                        footer={footer}
                    />
                );
            }
            if (classUpdateData.length === 0) {
                return (
                    <div className={splitDrawerStyles.emptyContainer}>
                        <EmptyState header="No class changes found" />
                    </div>
                );
            }
            return (
                <HistoryClassUpdateDetails
                    data={classUpdateData}
                    footer={footer}
                />
            );
        }

        if (isCatalogItem) {
            if (isDetailsError) {
                return (
                    <div className={splitDrawerStyles.emptyContainer}>
                        <EmptyState
                            header="Failed to load catalog item changes"
                            ctaText="Retry"
                            action={() => {
                                void refetchDetails();
                            }}
                        />
                    </div>
                );
            }
            if (catalogItemTable.rows.length === 0) {
                return (
                    <div className={splitDrawerStyles.emptyContainer}>
                        <EmptyState header="No catalog item changes found" />
                    </div>
                );
            }
            return (
                <HistoryCatalogItemDetails
                    key={catalogItemTable.kind}
                    table={catalogItemTable}
                    footer={footer}
                />
            );
        }

        if (isAccount) {
            if (accountTable.rows.length === 0) {
                return (
                    <div className={splitDrawerStyles.emptyContainer}>
                        <EmptyState header={accountEmptyStateHeader} />
                    </div>
                );
            }
            return (
                <HistoryAccountDetails
                    key={accountTable.kind}
                    table={accountTable}
                    emptyStateHeader={accountEmptyStateHeader}
                    footer={footer}
                />
            );
        }

        if (isDocument) {
            if (!detailsResponse?.data?.length) {
                return (
                    <div className={splitDrawerStyles.emptyContainer}>
                        <EmptyState header="No document changes found" />
                    </div>
                );
            }
            if (isDocumentDownload) {
                return (
                    <HistoryDocumentDownloadDetails
                        data={documentDownloadData}
                        footer={footer}
                    />
                );
            }
            const isDocumentCreateOrDelete = historyRow?.changeTypes?.every(
                (ct) =>
                    ct === AuditLogChangeType.CREATE ||
                    ct === AuditLogChangeType.DELETE,
            );
            if (isDocumentCreateOrDelete) {
                if (documentCreateDeleteData.length === 0) {
                    return (
                        <div className={splitDrawerStyles.emptyContainer}>
                            <EmptyState header="No document changes found" />
                        </div>
                    );
                }
                return (
                    <HistoryDocumentCreateDeleteDetails
                        data={documentCreateDeleteData}
                        footer={footer}
                    />
                );
            }
            if (documentUpdateData.length === 0) {
                return (
                    <div className={splitDrawerStyles.emptyContainer}>
                        <EmptyState header="No document changes found" />
                    </div>
                );
            }
            return (
                <HistoryDocumentUpdateDetails
                    data={documentUpdateData}
                    footer={footer}
                />
            );
        }

        if (isTask) {
            if (!detailsResponse?.data?.length) {
                return (
                    <div className={splitDrawerStyles.emptyContainer}>
                        <EmptyState header="No task changes found" />
                    </div>
                );
            }
            const isTaskCreateOrDelete = historyRow?.changeTypes?.every(
                (ct) =>
                    ct === AuditLogChangeType.CREATE ||
                    ct === AuditLogChangeType.DELETE,
            );
            if (isTaskCreateOrDelete) {
                return (
                    <HistoryTaskCreateDeleteDetails
                        logs={taskCreateDeleteLogs}
                        footer={footer}
                    />
                );
            }
            if (taskUpdateData.length === 0) {
                return (
                    <div className={splitDrawerStyles.emptyContainer}>
                        <EmptyState header="No task changes found" />
                    </div>
                );
            }
            return (
                <HistoryTaskUpdateDetails
                    data={taskUpdateData}
                    footer={footer}
                />
            );
        }

        if (isInvoiceTemplate) {
            return (
                <HistoryInvoiceTemplateDrawerContent
                    logs={detailsResponse?.data ?? []}
                    sourceType={historyRow?.sourceType ?? ""}
                    isError={isDetailsError}
                    onRetry={refetchDetails}
                    footer={footer}
                />
            );
        }

        if (isTransactionFieldReview) {
            return (
                <FieldReviewDrawerContent
                    data={detailsResponse?.data ?? []}
                    footer={footer}
                    entityIds={historyRow?.entityIds}
                />
            );
        }

        if (isTransactionRule) {
            if (ruleVersions.length === 0) {
                return (
                    <div className={splitDrawerStyles.emptyContainer}>
                        <EmptyState header="No rule changes found" />
                    </div>
                );
            }

            const isCreateOrDelete = historyRow?.changeTypes?.every(
                (ct) =>
                    ct === AuditLogChangeType.CREATE ||
                    ct === AuditLogChangeType.DELETE,
            );

            if (isCreateOrDelete) {
                return (
                    <HistoryRuleDetails
                        versions={ruleVersions}
                        footer={footer}
                    />
                );
            }

            return (
                <Fragment>
                    <div className={styles.viewTable}>
                        <ViewTable
                            data={ruleHistoryListRows}
                            view={
                                historyRow?.resourceType ===
                                ResourceType.TRANSFER_RULE
                                    ? transferRuleListView
                                    : ruleListView
                            }
                            config={
                                historyRow?.resourceType ===
                                ResourceType.TRANSFER_RULE
                                    ? transferRuleHistoryListViewConfig
                                    : ruleHistoryListViewConfig
                            }
                            onViewChange={(view) => {
                                if (
                                    historyRow?.resourceType ===
                                    ResourceType.TRANSFER_RULE
                                ) {
                                    setTransferRuleListView(view);
                                    return;
                                }
                                setRuleListView(view);
                            }}
                            onRowClick={handleSelectRuleHistoryRow}
                            emptyView={() => (
                                <EmptyState header="No rule changes found" />
                            )}
                            className={splitDrawerStyles.historyDetailsView}
                        />
                    </div>
                    {footer}
                </Fragment>
            );
        }

        if (isTransfer && isUpdated) {
            return (
                <HistoryPairedTransferDetails
                    data={updatedTransactionTableData}
                    footer={footer}
                    isMatched={isMatched}
                    selectedPairKeys={selectedPairKeys}
                    onPairSelectionChange={handlePairSelectionChange}
                    onPairOpen={handleOpenPair}
                />
            );
        }

        if (isAccrualMatch && detailsResponse?.data) {
            return (
                <HistoryAccrualMatchDetails
                    data={detailsResponse.data}
                    footer={footer}
                />
            );
        }

        if ((isUpdated || isAccrual || isTransactionDelete) && historyRow) {
            return (
                <HistoryUpdatedTransactionDetails
                    data={filteredUpdatedTransactionData}
                    footer={footer}
                    historyRow={historyRow}
                    rowSelection={transactionRowSelection}
                    onRowSelectionChange={setTransactionRowSelection}
                />
            );
        }

        if (isCreated && historyRow) {
            return (
                <HistoryCreatedTransactionDetails
                    data={filteredCreatedTransactionData}
                    footer={footer}
                    historyRow={historyRow}
                    rowSelection={transactionRowSelection}
                    onRowSelectionChange={setTransactionRowSelection}
                />
            );
        }

        return null;
    };

    const headerAddonLeft = useMemo(() => {
        if (!historyRow) {
            return null;
        }

        const pluralNoun = pluralizeWithCount(
            resourceNoun,
            headerResourceCount,
        );

        const fieldLabelFuncMap: Partial<
            Record<ResourceType, (field: string) => string | undefined>
        > = {
            [ResourceType.FINANCIAL_ACCOUNT]: getFinancialAccountFieldLabel,
            [ResourceType.COUNTERPARTY]: getCounterpartyFieldLabel,
            [ResourceType.TRANSACTION_RULE]: getRuleFieldLabel,
            [ResourceType.ACCOUNTING_RULE]: getRuleFieldLabel,
            [ResourceType.TRANSFER_RULE]: getRuleFieldLabel,
            [ResourceType.SPLIT_RULE]: getRuleFieldLabel,
            [ResourceType.TASK]: getTaskFieldLabel,
            [ResourceType.ACCOUNT]: getAccountFieldLabel,
            [ResourceType.ACCOUNT_GROUP]: getAccountFieldLabel,
            [ResourceType.CATALOG_ITEM]: getHistoryFieldLabel,
        };
        const getLabelFunc =
            fieldLabelFuncMap[historyRow.resourceType] ?? getFieldLabel;

        const fieldLabel =
            selectedFields.length > 0
                ? getLabelFunc(selectedFields[0])?.toLowerCase()
                : null;

        const verb = resolveHeaderVerb({
            sourceType: historyRow.sourceType,
            changeTypes: historyRow.changeTypes,
            resourceIds: historyRow.historyItem.resourceIds,
            sourceIds: historyRow.historyItem.sourceIds,
            templateVerb: resourceTemplate?.verb,
            isUpdated,
        });
        const showFieldLabel = fieldLabel && !resourceTemplate?.skipFieldLabel;
        const suffix = resourceTemplate?.suffix
            ? ` ${resourceTemplate.suffix}`
            : "";

        const actionText = showFieldLabel
            ? `${verb} ${fieldLabel} for ${pluralNoun}${suffix}`
            : `${verb} ${pluralNoun}${suffix}`;

        return (
            <ActivityDrawerHeader
                historyRow={historyRow}
                activityTitle={actionText}
            />
        );
    }, [
        historyRow,
        resourceNoun,
        headerResourceCount,
        resourceTemplate,
        selectedFields,
        isUpdated,
    ]);
    const isIdentityFeedRow =
        selectedFields.length > 0
            ? selectedFields.every((field) => field === HistoryField.ARCHIVED)
            : !historyRow?.changeTypes?.includes(AuditLogChangeType.UPDATE);
    const isAccountIdentityTable = detailsResponse
        ? accountTable.kind === HistoryAccountTableKind.IDENTITY
        : toInternalHistorySourceType(historyRow?.sourceType ?? "") !==
              APIProcessName.COA_ACCOUNT_MERGE && isIdentityFeedRow;
    const size = useMemo(() => {
        if (
            isCounterparty ||
            isTransactionRuleGroup ||
            isClass ||
            (isAccount && isAccountIdentityTable) ||
            isTask ||
            isInvoiceTemplate
        ) {
            return "half";
        }
        return "fullScreenWithNav";
    }, [
        isCounterparty,
        isTransactionRuleGroup,
        isClass,
        isAccount,
        isAccountIdentityTable,
        isTask,
        isInvoiceTemplate,
    ]);

    return (
        <Fragment>
            {revertTarget && (
                <ConfirmationModal
                    title="Undo change?"
                    question={
                        <RevertPreviewQuestion
                            fallbackCount={revertTarget.count}
                            previewCount={previewCount}
                            previewFields={previewFields}
                            primaryFields={historyRow?.fields ?? []}
                            isPreviewLoading={isPreviewLoading}
                        />
                    }
                    show
                    onHide={cancelRevert}
                    onReject={cancelRevert}
                    onConfirm={
                        isPreviewLoading
                            ? () => Promise.resolve()
                            : async () => {
                                  const isPartial =
                                      (revertTarget?.resourceIds?.length ?? 0) >
                                      0;
                                  const ok = await confirmRevert();
                                  if (ok && !isPartial) {
                                      handleClose();
                                  }
                              }
                    }
                    yes="Undo"
                    yesBtnVariant="primary"
                    no="Cancel"
                    noBtnVariant="secondary"
                />
            )}
            <Drawer
                open={open}
                onClose={handleClose}
                direction="right"
                onAnimationEnd={setIsOpen}
                headerAddonLeft={headerAddonLeft}
                size={size}
                noPadding
            >
                {shouldShowContent && historyRow && (
                    <div className={styles.drawerContent}>
                        {!isTransfer &&
                            !isAccrualMatch &&
                            !isCounterparty &&
                            !isTransactionRule &&
                            !isTransactionRuleGroup &&
                            !isClass &&
                            !isCatalogItem &&
                            !isAccount &&
                            !isDocument &&
                            !isTask &&
                            !isTransactionFieldReview &&
                            !isInvoiceTemplate && (
                                <div className={styles.toolbar}>
                                    <div className={styles.toolbarFilters}>
                                        <SearchFilter
                                            filter={filters.search}
                                            onChange={handleSearchChange}
                                            placeholder="Search changes..."
                                            minLength={2}
                                        />
                                    </div>
                                </div>
                            )}
                        {renderContent()}
                    </div>
                )}
            </Drawer>

            <Drawer
                open={!!selectedRuleLogId}
                onClose={handleCloseRuleDetailDrawer}
                direction="right"
                size="fullScreenWithNav"
                noPadding
                headerAddonLeft={
                    <StepTrailBreadcrumbs
                        steps={RULE_HISTORY_BREADCRUMB_STEPS}
                        currentStep="details"
                        onStepChange={handleRuleHistoryBreadcrumbStepChange}
                    />
                }
            >
                {selectedRuleLogId && (
                    <div className={splitDrawerStyles.drawerContent}>
                        <div className={splitDrawerStyles.content}>
                            <HistoryRuleDetails
                                versions={selectedRuleVersions}
                                footer={null}
                            />
                        </div>
                    </div>
                )}
            </Drawer>
        </Fragment>
    );
};
